import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { BadgePercent, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OffersStrip = () => {
    const [offers, setOffers] = useState([]);

    useEffect(() => {
        axios
            .get(`${API}/offers`)
            .then((res) => setOffers(res.data || []))
            .catch(() => setOffers([]));
    }, []);

    if (offers.length === 0) return null;

    return (
        <section id="offers" className="py-20 md:py-28 px-6 md:px-10 bg-[#1C1C1E]" data-testid="offers-section">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="flex flex-wrap items-end justify-between gap-6"
                >
                    <div>
                        <img src="/brand/esak-logo-white.webp" alt="Esak" className="h-11 w-auto mb-5" data-testid="offers-esak-logo" />
                        <p className="font-serif-brand italic text-xl mb-3" style={{ color: "#E50046" }}>Good vibes. Great deals.</p>
                        <h2 className="font-syne text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="offers-headline">
                            Happy Hours at Esak
                        </h2>
                    </div>
                    <BadgePercent size={44} style={{ color: "#E50046" }} className="hidden md:block" />
                </motion.div>

                <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="offers-grid">
                    {offers.map((o, i) => (
                        <motion.article
                            key={o.id}
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.6, delay: i * 0.12 }}
                            className="relative rounded-[2rem] bg-white/[0.06] border border-white/10 p-8 flex flex-col hover:-translate-y-1.5 hover:bg-white/[0.09] transition-[transform,background-color] duration-300 overflow-hidden"
                            data-testid={`offer-card-${o.id}`}
                        >
                            {o.image_path && (
                                <img
                                    src={`${API}/files/${o.image_path}`}
                                    alt={o.title}
                                    className="rounded-2xl w-full h-44 object-cover mb-6"
                                    loading="lazy"
                                />
                            )}
                            {o.badge && (
                                <span className="self-start rounded-full px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-white mb-5" style={{ backgroundColor: "#E50046" }} data-testid={`offer-badge-${o.id}`}>
                                    {o.badge}
                                </span>
                            )}
                            <h3 className="font-syne text-xl md:text-2xl font-bold text-white leading-snug" data-testid={`offer-title-${o.id}`}>{o.title}</h3>
                            {o.price && <p className="mt-1 font-serif-brand italic text-xl" style={{ color: "#F0841B" }} data-testid={`offer-price-${o.id}`}>{o.price}</p>}
                            <ul className="mt-5 space-y-2.5 flex-1" data-testid={`offer-lines-${o.id}`}>
                                {(o.lines || []).map((line, j) => (
                                    <li key={j} className="text-sm leading-relaxed text-white/70 flex gap-2.5">
                                        <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: "#E50046" }} />
                                        {line}
                                    </li>
                                ))}
                            </ul>
                            {o.booking_link && (
                                <a
                                    href={o.booking_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#F0841B] transition-colors duration-200"
                                    data-testid={`offer-link-${o.id}`}
                                >
                                    Grab this offer <ArrowUpRight size={16} />
                                </a>
                            )}
                            {o.terms && <p className="mt-5 text-[11px] leading-relaxed text-white/35" data-testid={`offer-terms-${o.id}`}>{o.terms}</p>}
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};
